import { useEffect, useState } from "react";
import { Minus, Plus } from "lucide-react";
import { cn } from "@tsi/ui";

// Escala da plataforma (emula o Ctrl+ do navegador), de 80% a 130% em passos de 10%. O padrão
// segue a POC (110%). Fica guardada no navegador. Expõe o fator em --zoom para compensar 100vh em
// containers fixos (sidebar, modais).

const MIN = 80;
const MAX = 130;
const PASSO = 10;
const PADRAO = 110;
const STORAGE_KEY = "tsi:zoom";

function lerZoom(): number {
  try {
    const v = Number(localStorage.getItem(STORAGE_KEY));
    return v >= MIN && v <= MAX ? v : PADRAO;
  } catch {
    return PADRAO;
  }
}

export function useZoom() {
  const [zoom, setZoom] = useState(lerZoom);
  useEffect(() => {
    const fator = String(zoom / 100);
    document.documentElement.style.zoom = fator;
    document.documentElement.style.setProperty("--zoom", fator);
    try {
      localStorage.setItem(STORAGE_KEY, String(zoom));
    } catch {
      // sem storage: vale só nesta aba
    }
    return () => {
      document.documentElement.style.zoom = "1";
      document.documentElement.style.setProperty("--zoom", "1");
    };
  }, [zoom]);
  return [zoom, setZoom] as const;
}

/** − [100%] + no topo. Clicar no número volta para 100%. */
export function ZoomControl({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const botao =
    "inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:pointer-events-none disabled:opacity-40";
  return (
    <div
      className="hidden h-8 items-center gap-0.5 rounded-lg border border-border/70 bg-card p-0.5 shadow-card sm:inline-flex"
      role="group"
      aria-label="Zoom da plataforma"
    >
      <button
        type="button"
        className={botao}
        onClick={() => onChange(Math.max(MIN, value - PASSO))}
        disabled={value <= MIN}
        title="Diminuir zoom"
        aria-label="Diminuir zoom"
      >
        <Minus className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        onClick={() => onChange(100)}
        title="Voltar para 100%"
        aria-label={`Zoom em ${value}%. Voltar para 100%`}
        className={cn(
          "inline-flex h-7 min-w-[46px] items-center justify-center rounded-md px-1 text-[11.5px] font-semibold tabular-nums text-foreground transition-colors hover:bg-secondary",
        )}
      >
        {value}%
      </button>
      <button
        type="button"
        className={botao}
        onClick={() => onChange(Math.min(MAX, value + PASSO))}
        disabled={value >= MAX}
        title="Aumentar zoom"
        aria-label="Aumentar zoom"
      >
        <Plus className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
